import { createFileRoute, useParams } from "@tanstack/react-router";
import { trpc, type RouterInputs } from "@/hooks/trpc";
import type { FermentationProtocolId } from "@brewbase/db/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArchiveRestoreIcon } from "lucide-react";
import { toast } from "sonner";
import { useAutosave, filterDefined } from "@/hooks/use-autosave";

export const Route = createFileRoute(
  "/fermentations/protocols/$protocol_id/overview",
)({
  component: OverviewPage,
});

type UpdateInput = RouterInputs["fermentations"]["protocols"]["update"];

function OverviewPage() {
  const { protocol_id } = useParams({ from: Route.id });
  const protocolId = protocol_id as unknown as FermentationProtocolId;

  const { data: protocol, isLoading } =
    trpc.fermentations.protocols.get.useQuery(protocolId);

  const { save } = useAutosave({
    mutation: trpc.fermentations.protocols.update,
    query: trpc.fermentations.protocols.get,
    queryInput: protocolId,
    updateCache: (input, old) =>
      old ? { ...old, ...filterDefined(input) } : old,
    onError: (err) => toast.error(`Failed to update: ${err.message}`),
  });

  const isArchived = !!protocol?.archived_at;

  const utils = trpc.useUtils();
  const unarchiveMutation = trpc.fermentations.protocols.unarchive.useMutation({
    onSuccess: () => {
      toast.success("Protocol unarchived.");
      utils.fermentations.protocols.get.invalidate(protocolId);
      utils.fermentations.protocols.list.invalidate();
      utils.fermentations.protocols.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to unarchive: ${error.message}`);
    },
  });

  const update = (field: keyof UpdateInput, value: unknown) => {
    if (protocol?.is_locked || isArchived) return;
    save({ id: protocolId, [field]: value } as UpdateInput);
  };

  if (isLoading || !protocol) {
    return (
      <div className="p-6 max-w-3xl space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <Skeleton className="h-32" />
      </div>
    );
  }

  const isDisabled = protocol.is_locked || isArchived;

  return (
    <div className="p-6 max-w-3xl">
      {isArchived && (
        <div className="flex items-center justify-between bg-olive-2 px-4 py-2 mb-6 text-sm text-olive-11">
          <span>This protocol is archived.</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => unarchiveMutation.mutate(protocolId)}
            disabled={unarchiveMutation.isPending}
          >
            <ArchiveRestoreIcon className="h-3 w-3 mr-1" />
            Unarchive
          </Button>
        </div>
      )}
      <h2 className="text-lg font-semibold mb-8">Protocol Overview</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-sm font-medium">
            Name
          </Label>
          <Input
            id="name"
            value={protocol.name ?? ""}
            onChange={(e) => update("name", e.target.value)}
            disabled={isDisabled}
            placeholder="Protocol name"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="version" className="text-sm font-medium">
            Version
          </Label>
          <Input
            id="version"
            value={protocol.version ?? ""}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (v) update("version", v);
            }}
            disabled={isDisabled}
            placeholder="e.g., 1.0"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mode" className="text-sm font-medium">
            Mode
          </Label>
          <Select
            value={protocol.mode}
            onValueChange={(val) => update("mode", val)}
            disabled={isDisabled}
          >
            <SelectTrigger id="mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BATCH">Batch</SelectItem>
              <SelectItem value="FED_BATCH">Fed-Batch</SelectItem>
              <SelectItem value="CONTINUOUS">Continuous</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t my-8" />

      <div className="space-y-1.5">
        <Label htmlFor="description" className="text-sm font-medium">
          Description
        </Label>
        <Textarea
          id="description"
          value={protocol.description ?? ""}
          onChange={(e) => update("description", e.target.value || null)}
          disabled={isDisabled}
          placeholder="Describe this protocol's purpose and when to use it..."
          rows={4}
          className="resize-none"
        />
      </div>

      <div className="border-t my-8" />

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          Created {new Date(protocol.created_at).toLocaleDateString()}
        </span>
        <span>
          Updated {new Date(protocol.updated_at).toLocaleDateString()}
        </span>
        {protocol.derived_from_id && (
          <span>Derived from protocol #{protocol.derived_from_id}</span>
        )}
      </div>
    </div>
  );
}
